import {
  AUTO_COMBAT_DEFAULTS,
  type AutoCombatDef,
  type CombatHudRaidFields,
  type CombatHudSquadAiFields,
  type FieldErrors,
  PLAYER_DISPOSITIONS,
  RAID_CAMERA_DEFAULTS,
  RAID_HUD_DEFAULTS,
  RAID_SPRINT_DEFAULTS,
  type RaidCameraDef,
  type RaidHudDef,
  SQUAD_AI_COMBAT_HUD_DEFAULTS,
  SQUAD_REMOTE_HEAL_DEFAULT,
  TEAM_START_DISPOSITIONS,
} from '../../lib/validate';
import { CheckboxField, Fieldset, NumberField, SelectField } from './fields';

/*
 * Combat HUD overhaul content (Wave 0 contract; shared-cs CombatHudDefs.cs, validate/combatHud.ts). Absent fields show the C#
 * defaults. Rendered by RaidRulesForm and SquadRulesForm (SectionForms.tsx).
 */

type NumRow = [key: string, label: string, hint: string, step?: number];

function NumberRows(props: {
  value: object;
  rows: NumRow[];
  prefix: string;
  errors: FieldErrors;
  onSet: (k: string, v: number) => void;
}) {
  return (
    <>
      {props.rows.map(([k, label, hint, step]) => (
        <NumberField
          key={k}
          label={label}
          hint={hint}
          min={0}
          step={step ?? 0.05}
          value={(props.value as Record<string, number | undefined>)[k]}
          issues={props.errors[props.prefix + k]}
          onChange={(v) => props.onSet(k, v ?? 0)}
        />
      ))}
    </>
  );
}

const playerLabels: Record<string, string> = { hot: 'HOT', aim: 'AIM', hold: 'HOLD' };
const teamLabels: Record<string, string> = {
  clearHot: 'CLEAR HOT',
  silent: 'SILENT',
  hold: 'HOLD',
};

/** raidRules: the sprint ramp, the horde warning, autoCombat, camera and hud. */
export function RaidCombatHudFieldsets<R extends object>({
  value,
  onChange,
  errors,
}: {
  value: R;
  onChange: (next: R) => void;
  errors: FieldErrors;
}) {
  const r = value as R & CombatHudRaidFields;
  const flat = { ...RAID_SPRINT_DEFAULTS, ...r };
  const ac: AutoCombatDef = { ...AUTO_COMBAT_DEFAULTS, ...(r.autoCombat ?? {}) };
  const cam: RaidCameraDef = { ...RAID_CAMERA_DEFAULTS, ...(r.camera ?? {}) };
  const hud: RaidHudDef = { ...RAID_HUD_DEFAULTS, ...(r.hud ?? {}) };
  const set = (patch: CombatHudRaidFields) => onChange({ ...r, ...patch });

  return (
    <>
      <Fieldset legend="One-stick movement">
        <NumberRows
          value={flat}
          prefix=""
          errors={errors}
          onSet={(k, v) => set({ [k]: v })}
          rows={[
            [
              'sprintLeanMin',
              'Sprint lean',
              '0.5..1: the stick lean that counts as full lean.',
              0.01,
            ],
            [
              'sprintRampDelayS',
              'Sprint ramp delay (s)',
              'Seconds at full lean before sprint starts (0..2).',
            ],
            [
              'sprintRampS',
              'Sprint ramp (s)',
              'Seconds the walk-to-sprint blend takes (0..2; 0 = instant).',
            ],
            [
              'hordeWarnAt',
              'THEY HEAR YOU at',
              'Horde meter share for the one-shot warning toast. 0 = off; below 1.',
            ],
          ]}
        />
      </Fieldset>
      <Fieldset legend="Auto-combat (player)">
        <NumberRows
          value={ac}
          prefix="autoCombat."
          errors={errors}
          onSet={(k, v) => set({ autoCombat: { ...ac, [k]: v } })}
          rows={[
            [
              'acquireS',
              'Acquire (s)',
              'Seconds before the auto-aim takes a new best target (0..2).',
            ],
            [
              'retargetHysteresis',
              'Retarget hysteresis',
              'A new target must score this share better to switch (0..1).',
            ],
            [
              'targetRangeMult',
              'Range × weapon range',
              "Auto-attack reach as a share of the weapon's range (above 0, at most 1.5).",
            ],
            [
              'steadyAutoMult',
              'Steady aim while auto-aiming',
              'Headshot steady build-up × this while HOT and moving (0..1; 0 = none).',
            ],
          ]}
        />
        <CheckboxField
          label="On-screen targets only"
          hint="Only zombies inside the camera view are picked (matters in portrait)."
          checked={ac.onScreenOnly ?? true}
          onChange={(on) => set({ autoCombat: { ...ac, onScreenOnly: on } })}
        />
        <SelectField
          label="Default player disposition"
          hint="When the device has none saved."
          value={ac.defaultDisposition}
          options={PLAYER_DISPOSITIONS.map((d) => ({ value: d, label: playerLabels[d] ?? d }))}
          issues={errors['autoCombat.defaultDisposition']}
          onChange={(v) => set({ autoCombat: { ...ac, defaultDisposition: v ?? 'hot' } })}
        />
      </Fieldset>
      <Fieldset legend="Raid camera and HUD">
        <NumberRows
          value={cam}
          prefix="camera."
          errors={errors}
          onSet={(k, v) => set({ camera: { ...cam, [k]: v } })}
          rows={[
            [
              'farViewWM',
              'FAR view width (m)',
              'The default zoom: narrowest view across the view plane at the FAR distance (4..60; binds in portrait).',
              0.1,
            ],
            [
              'farViewHM',
              'FAR view height (m)',
              'The default zoom: shortest view (4..60; binds in landscape; 9.5 at 20 m).',
              0.1,
            ],
            [
              'farDistance',
              'FAR distance (m)',
              'How far the FAR camera sits from the player (10..80; 20 = more perspective).',
              0.5,
            ],
            [
              'nearViewWM',
              'NEAR view width (m)',
              'The close zoom: narrowest view at the NEAR distance (4..60; binds in portrait).',
              0.1,
            ],
            [
              'nearViewHM',
              'NEAR view height (m)',
              'The close zoom: shortest view (4..60; 7 at 40 m).',
              0.1,
            ],
            [
              'nearDistance',
              'NEAR distance (m)',
              'How far the NEAR camera sits from the player (10..80; 40 = the flat comic-panel read).',
              0.5,
            ],
            [
              'minViewWM',
              'Legacy min view width (m)',
              'Unused since the near/far zoom (ADR 0068); kept so older bundles validate (4..60).',
              0.1,
            ],
            [
              'minViewHM',
              'Legacy min view height (m)',
              'Unused since the near/far zoom (ADR 0068) (4..60).',
              0.1,
            ],
            [
              'leadEaseS',
              'Lead ease (s)',
              'Seconds the lead toward the aim takes to reach 95% of a new aim (0.05..3).',
              0.05,
            ],
            [
              'leadMaxMps',
              'Lead max speed (m/s)',
              'Fastest the lead may move, so an auto-aim retarget never whips the view (0.5..20).',
              0.1,
            ],
            [
              'leadHoldS',
              'Lead hold (s)',
              'Seconds the lead holds when the aim drops (target dead) before easing back (0..3).',
              0.05,
            ],
          ]}
        />
        <NumberRows
          value={hud}
          prefix="hud."
          errors={errors}
          onSet={(k, v) => set({ hud: { ...hud, [k]: v } })}
          rows={[
            [
              'hubDwellS',
              'Hub dwell (s)',
              'Rest on a YOU wedge this long to latch it and carry on (0.05..1).',
            ],
          ]}
        />
      </Fieldset>
    </>
  );
}

/** squadRules: remoteHealM, and the team-disposition and hunt & kill keys of ai. */
export function SquadCombatHudFieldsets<S extends object>({
  value,
  onChange,
  errors,
}: {
  value: S;
  onChange: (next: S) => void;
  errors: FieldErrors;
}) {
  const s = value as S & { remoteHealM?: number; ai?: object | null };
  const ai = { ...SQUAD_AI_COMBAT_HUD_DEFAULTS, ...((s.ai ?? {}) as CombatHudSquadAiFields) };
  const setAi = (patch: CombatHudSquadAiFields) =>
    onChange({ ...s, ai: { ...(s.ai ?? {}), ...ai, ...patch } });

  return (
    <Fieldset legend="Team dispositions and hunt & kill">
      <NumberField
        label="Portrait heal range (m)"
        hint="How far the HealPop + reaches (from the revive range up to 30)."
        min={0}
        step={0.5}
        value={s.remoteHealM ?? SQUAD_REMOTE_HEAL_DEFAULT}
        issues={errors.remoteHealM}
        onChange={(v) => onChange({ ...s, remoteHealM: v ?? 0 })}
      />
      <SelectField
        label="Default team disposition"
        hint="When the device has none saved. Never KILL: hunt & kill is never restored at raid start."
        value={ai.defaultTeamDisposition}
        options={TEAM_START_DISPOSITIONS.map((d) => ({ value: d, label: teamLabels[d] ?? d }))}
        issues={errors['ai.defaultTeamDisposition']}
        onChange={(v) => setAi({ defaultTeamDisposition: v ?? 'clearHot' })}
      />
      <NumberRows
        value={ai}
        prefix="ai."
        errors={errors}
        onSet={(k, v) => setAi({ [k]: v })}
        rows={[
          [
            'holdAvoidM',
            'HOLD: keep away (m)',
            'Teammates steer to stay this far from zombies (0..15).',
            0.5,
          ],
          [
            'silentTakedownMaxM',
            'SILENT: takedown reach (m)',
            'Farthest a teammate goes for an AI takedown (0..30; 0 = none).',
            0.5,
          ],
          [
            'huntSweptSeenFrac',
            'Hunt: room seen share',
            'A room is swept once this share of it was seen (above 0, at most 1).',
          ],
          ['huntRepathS', 'Hunt: repath (s)', 'Least time between path recomputes (0.1..10).', 0.1],
          [
            'huntGiveUpS',
            'Hunt: give up after (s)',
            'A room that will not sweep, or a zombie out of reach, is given up after this long (5..300).',
            1,
          ],
          [
            'huntAreaClearToastS',
            'AREA CLEAR toast (s)',
            'How long the toast shows (0.5..10).',
            0.5,
          ],
        ]}
      />
      <CheckboxField
        label="Hunters force locked doors"
        hint="Loud (breakLockedDoor noise). Scenario doors are never forced."
        checked={ai.huntForceLockedDoors}
        onChange={(on) => setAi({ huntForceLockedDoors: on })}
      />
      <CheckboxField
        label="Hunt lifts the kill-share cap"
        hint="Hunters skip the governor's soft and hard caps, so a hunt can finish."
        checked={ai.huntLiftsKillCap}
        onChange={(on) => setAi({ huntLiftsKillCap: on })}
      />
      <CheckboxField
        label="Hunters home in on the last zombies"
        hint="Once every room is swept, hunters track the zombies left out of sight, so a hunt always ends."
        checked={ai.huntHomeInOnLast}
        onChange={(on) => setAi({ huntHomeInOnLast: on })}
      />
    </Fieldset>
  );
}
